import { useState, useMemo } from "react";
import type { Auction } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { useToast } from "@/context/ToastContext";
import { auctionService } from "@/services";
import { computeCommission } from "@/utils/auction";
import { Card } from "@/components/base/Card";
import { Button } from "@/components/base/Button";
import { Alert } from "@/components/base/Alert";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { HeaderBand } from "./HeaderBand";

interface ReservedCyclePanelProps {
  auction: Auction;
  onClosed: () => void;
}

/**
 * The reserved organizer cycle. There is no auction at all — the organizer
 * receives the full pool (split into prize amount and organizer commission if configured),
 * so no dividend is generated and every member pays the full installment.
 */
export function ReservedCyclePanel({ auction, onClosed }: ReservedCyclePanelProps) {
  const { user, isViewer } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();
  const actor = user?.name ?? "Staff User";
  const [closing, setClosing] = useState(false);

  const commission = useMemo(() => {
    return computeCommission(auction.commissionType, auction.commissionValue, auction.pool, 0);
  }, [auction.pool, auction.commissionType, auction.commissionValue]);

  const commPerMember = auction.memberCount > 0 ? Math.floor(commission / auction.memberCount) : 0;
  const totalCommission = commPerMember * auction.memberCount;
  const memberInstallment = auction.monthlyInstallment + commPerMember;
  const totalCollection = memberInstallment * auction.memberCount;
  const organizerTotalPayout = auction.pool + totalCommission;

  const handleConfirm = async () => {
    const ok = await confirm({
      title: "Complete the reserved cycle?",
      message: (
        <>
          <strong>{auction.organizer?.fullName ?? "The organizer"}</strong> will receive the whole amount including commission:{" "}
          <span className="tnum font-bold">{organizerTotalPayout.toLocaleString("en-IN")}</span>
          {totalCommission > 0 ? (
            <> (Gross Pool: <span className="tnum">{auction.pool.toLocaleString("en-IN")}</span> + Commission: <span className="tnum">{totalCommission.toLocaleString("en-IN")}</span>)</>
          ) : null}
          . Each member will pay <span className="tnum font-semibold">{memberInstallment.toLocaleString("en-IN")}</span> (Base: {auction.monthlyInstallment.toLocaleString("en-IN")} + Commission: {commPerMember.toLocaleString("en-IN")}).
        </>
      ),
      confirmLabel: "Complete cycle",
      destructive: true,
    });
    if (!ok) return;
    setClosing(true);
    try {
      await auctionService.closeReservedCycle(auction.groupId, auction.cycle, actor);
      toast.success("Reserved cycle completed", `${auction.groupName} cycle ${auction.cycle} is now locked in.`);
      onClosed();
    } catch (err) {
      toast.error("Could not complete cycle", err instanceof Error ? err.message : "Please try again.");
      setClosing(false);
    }
  };

  return (
    <div>
      <HeaderBand
        auction={auction}
        pool={auction.pool}
        currentLowest={auction.pool}
        currentInstallment={memberInstallment}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card title="Reserved organizer cycle" subtitle={`Cycle ${auction.cycle} · ${auction.periodLabel}`}>
            <div className="space-y-4">
              <Alert variant="info" title="No auction is held this cycle">
                This is the organizer's reserved cycle. There is no bidding and no discount — the organizer receives the full pool plus the cycle commission.
              </Alert>

              <div className="rounded-md bg-background-100 px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-foreground-500">Organizer</p>
                <p className="text-lg font-semibold text-foreground-950">{auction.organizer?.fullName ?? "—"}</p>
                <p className="tnum text-sm text-foreground-600">Ticket {auction.organizer?.ticketNumber ?? "—"}</p>
              </div>

              <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-4">
                <div>
                  <dt className="text-xs text-foreground-500">Gross pool</dt>
                  <dd className="tnum mt-0.5 text-xl font-bold text-accent-900"><CurrencyDisplay amount={auction.pool} hideSymbol /></dd>
                </div>
                {totalCommission > 0 ? (
                  <div>
                    <dt className="text-xs text-foreground-500">Commission (+)</dt>
                    <dd className="tnum mt-0.5 text-xl font-bold text-primary-700"><CurrencyDisplay amount={totalCommission} hideSymbol /></dd>
                  </div>
                ) : null}
                <div>
                  <dt className="text-xs text-foreground-500">Organizer total payout</dt>
                  <dd className="tnum mt-0.5 text-xl font-bold text-success-700"><CurrencyDisplay amount={organizerTotalPayout} hideSymbol /></dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">Member installment</dt>
                  <dd className="tnum mt-0.5 text-xl font-bold text-foreground-900"><CurrencyDisplay amount={memberInstallment} hideSymbol /></dd>
                </div>
              </dl>

              <p className="text-sm text-foreground-600">
                Every member pays{" "}
                <span className="tnum font-semibold">{memberInstallment.toLocaleString("en-IN")}</span>
                {commPerMember > 0 ? (
                  <> (Base: {auction.monthlyInstallment.toLocaleString("en-IN")} + Commission share: {commPerMember.toLocaleString("en-IN")})</>
                ) : null}{" "}
                this month. Total collected ({totalCollection.toLocaleString("en-IN")}) is paid to the organizer account.
              </p>

              <div className="flex items-center justify-end gap-2 border-t border-background-100 pt-4">
                <Button
                  variant="danger"
                  icon={<i className="ri-check-double-line" />}
                  loading={closing}
                  disabled={isViewer}
                  onClick={handleConfirm}
                >
                  Complete reserved cycle
                </Button>
              </div>
            </div>
          </Card>
        </div>

        <div>
          <Card title="This month's result" subtitle="At a glance">
            <dl className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-foreground-600">Base pool</dt>
                <dd className="tnum font-bold text-foreground-700"><CurrencyDisplay amount={auction.pool} hideSymbol /></dd>
              </div>
              {totalCommission > 0 ? (
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-600">Commission collected (+)</dt>
                  <dd className="tnum font-bold text-primary-700"><CurrencyDisplay amount={totalCommission} hideSymbol /></dd>
                </div>
              ) : null}
              <div className="flex items-center justify-between border-t border-background-200 pt-2">
                <dt className="text-foreground-600">Total expected collection</dt>
                <dd className="tnum font-bold text-accent-900"><CurrencyDisplay amount={totalCollection} hideSymbol /></dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-600">Payout to organizer</dt>
                <dd className="tnum font-bold text-success-700"><CurrencyDisplay amount={organizerTotalPayout} hideSymbol /></dd>
              </div>
            </dl>
            <p className="mt-3 text-xs text-success-700">Collection exactly reconciles with organizer total payout.</p>
          </Card>
        </div>
      </div>
    </div>
  );
}